import { ConfirmDialog } from "../../../../components/shared";
import { CreateFieldStyled } from "./GroupMainPageStyle";
import { useFormikContext } from "formik";
import { commonPropsForInputsWithoutValue } from "../../../booking-admin/reasons/helpers";
import { OwcButton, OwcInput } from "@one/react";
import { OwcSelectDropdown, OwcListItem, OwcCheckbox, OwcTypography } from "@one/react";
import gql from "graphql-tag";
import { withApollo } from "react-apollo";
import { useEffect, useRef, useState } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { debounce } from "lodash";
import { CircularProgress } from "@mui/material";
import Popper from "@mui/material/Popper";
import { snackbarService } from "@one/web-components/dist/index.cjs.js";
import { DURATION } from "../../../../constants";
import { formatUserDisplay } from "../../../../utils/helpers/text";

const CIDM_LIMIT = 50;

const GET_CIDM_GROUPS_NAMES = gql`
  query GetCidmGroupsNames($input: CidmGroupQueryInput!) {
    getCidmGroupsNames(input: $input) {
      groupId
      groupName
    }
  }
`;

const CustomPopper = (props) => {
  return <Popper {...props} placement="bottom" />;
};

const GroupDialog = ({ open, isEditMode, onCancel, submit, userList, loading, client }) => {
  const [selectResetKey, setSelectResetKey] = useState(0);
  const formik = useFormikContext();
  const classes = {};
  const isEdit = isEditMode;

  const [cidmOptions, setCidmOptions] = useState([]);
  const [cidmSearchLoading, setCidmSearchLoading] = useState(false);
  const [limitReached, setLimitReached] = useState(false);

  useEffect(() => {
    if (
      formik.values.groupNameCIDM &&
      formik.values.groupId &&
      formik.values.useCIDMMembership &&
      isEditMode &&
      !cidmOptions.length
    ) {
      setCidmOptions([
        {
          groupName: formik.values.groupNameCIDM,
          groupId: formik.values.groupId,
        },
      ]);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values, open]);

  useEffect(() => {
    if (!open) {
      formik.resetForm();
      setCidmOptions([]);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const debouncedFetchGroups = useRef(
    debounce(async (value) => {
      setCidmSearchLoading(true);
      try {
        const { data } = await client.query({
          query: GET_CIDM_GROUPS_NAMES,
          variables: {
            input: { groupName: value },
          },
          fetchPolicy: "no-cache",
        });
        const list = data?.getCidmGroupsNames ?? [];
        setCidmOptions(list);
        //FIXME: quick detection of limit reached until Backend can send that flag
        setLimitReached(list.length === CIDM_LIMIT);
      } catch (err) {
        console.error("CIDM autocomplete error", err);
        snackbarService.show({
          type: "error",
          message: "Unable to load CIDM groups. Please try again later.",
          duration: DURATION,
        });
        setCidmOptions([]);
        setLimitReached(false);
      } finally {
        setCidmSearchLoading(false);
      }
    }, 300)
  ).current;

  const handleCIDMCheckboxChange = (e) => {
    const isChecked = e.target.checked;
    formik.setFieldValue("useCIDMMembership", isChecked);

    if (isChecked) {
      formik.setFieldValue("users", null);
      setSelectResetKey((prev) => prev + 1);
    }
  };

  return (
    <ConfirmDialog
      open={open}
      close={onCancel}
      approveText={isEditMode ? "Update" : "Create"}
      approveColor="primary"
      approveVariant="contained"
      cancelText="Cancel"
      cancelVariant="outlined"
      cancelColor="primary"
      onCancel={onCancel}
      isDivider
      size="md"
      testid={"import-file-dialog"}
      title={isEditMode ? "Edit booking group" : "Create booking group"}
      renderActions={({ cancelText, approveText }) => (
        <div style={{ paddingBottom: 8, display: "flex", marginLeft: "auto" }}>
          <OwcButton
            data-testid="confirm-dialog-actions-button-cancel"
            onClick={onCancel}
            variant="secondary"
            style={{ marginRight: 32 }}
          >
            {cancelText}
          </OwcButton>
          <OwcButton
            data-testid="confirm-dialog-actions-button-approve"
            onClick={() => {
              submit(formik.values);
            }}
            variant="primary"
            disabled={
              loading ||
              !formik.values.groupName?.trim() ||
              (formik.values.useCIDMMembership
                ? (
                  !formik.values.groupNameCIDM?.trim() ||
                  !formik.values.cidmRequestUrl?.trim() ||
                  !formik.values.groupId?.trim()
                )
                : !formik.values.users?.length
              )
            }
          >
            {approveText}
          </OwcButton>
        </div>
      )}
    >
      <CreateFieldStyled>
        <OwcInput
          data-testid={`text-field-group-name`}
          label="Enter booking group name *"
          className="TextFieldInput"
          size="small"
          style={{ marginRight: "20px" }}
          name="groupName"
          value={formik.values?.groupName}
          onInputChange={formik.handleChange}
          onBlur={formik.handleBlur}
          disabled={isEditMode}
          required
          {...commonPropsForInputsWithoutValue(classes, formik, "groupName", "groupName", "text-field", isEdit)}
        >
          {formik.errors["groupName"] && formik.touched["groupName"] === true && (
            <>
              <span slot="error-text">{formik.errors["groupName"]}</span>
            </>
          )}
        </OwcInput>
        <OwcSelectDropdown
          key={`dropdown-${selectResetKey}`}
          multiple
          label="Select user"
          dataTestid={`users-inputs-users-input`}
          style={{ width: 250 }}
          required={!formik.values?.useCIDMMembership}
          name="users"
          disabled={formik.values?.useCIDMMembership}
          onSelectChange={(e) => {
            if (!formik.values?.useCIDMMembership) {
              let selected = e.detail.map((v) => userList[v]);
              formik.setFieldValue("users", selected, true);
            }
          }}
        >
          {userList.map((list, index) => {
            return (
              <OwcListItem
                selected={formik.values?.users === null ? false : (formik.values?.users).includes(list) ? true : false}
                disabled={formik.values?.useCIDMMembership}
                data-testid={"admin-panel-modal-select-run-mode-menu-item-" + list.value}
                key={`${list.value}-${index}`}
                value={list.value}
              >
                <OwcCheckbox disabled={formik.values?.useCIDMMembership}>
                  {formatUserDisplay(list)}
                </OwcCheckbox>
              </OwcListItem>
            );
          })}
        </OwcSelectDropdown>
      </CreateFieldStyled>

      <div style={{ marginTop: "16px" }}>
        <OwcCheckbox
          name="useCIDMMembership"
          disabled={formik.values?.users?.length || loading}
          checked={formik.values?.useCIDMMembership}
          onInputChange={handleCIDMCheckboxChange}
          onClick={(e) => e.stopPropagation()}
        >
          Use CIDM Membership type
        </OwcCheckbox>
      </div>

      {formik.values?.useCIDMMembership && (
        <>
          {limitReached && (
            <OwcTypography
              variant="badge"
              style={{ color: '#ed6c02', fontFamily: 'inherit', fontSize: 'small', marginBottom: '8px', fontWeight: '500' }}
            >
              Only first 50 are displayed – please narrow down your search
            </OwcTypography>
          )}
          <div style={{ display: "flex", marginBottom: "10px", gap: "16px" }}>
            <Autocomplete
              key={formik.values?.groupNameCIDM}
              sx={{ flex: 1, minWidth: 0 }}
              value={cidmOptions.find((o) => o.groupName === formik.values.groupNameCIDM) || null}
              PopperComponent={CustomPopper}
              disablePortal
              options={cidmOptions}
              getOptionLabel={(option) => option.groupName}
              loading={cidmSearchLoading}
              isOptionEqualToValue={(opt, val) => opt?.groupId === val?.groupId}
              onInputChange={(_, v, reason) => {
                if (reason === "input" && v.length >= 4) debouncedFetchGroups(v);
              }}
              onChange={(event, value) => {
                formik.setFieldValue('groupNameCIDM', value?.groupName || '');
                formik.setFieldValue('groupId', value?.groupId || '');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  label="CIDM Group Name"
                  required={formik.values?.useCIDMMembership}
                  size="small"
                  value={formik.values?.groupNameCIDM}
                  InputProps={{
                    ...params.InputProps,
                    sx: {
                      input: {
                        paddingLeft: "8px",
                      },
                    },
                    endAdornment: (
                      <>
                        {cidmSearchLoading ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <OwcInput
              label="Request URL *"
              size="small"
              style={{ flex: 1 }}
              name="cidmRequestUrl"
              value={formik.values?.cidmRequestUrl || ""}
              onInputChange={formik.handleChange}
              onBlur={formik.handleBlur}
              required={formik.values?.useCIDMMembership}
            />
          </div>

          <div style={{ display: "flex", marginTop: "16px", gap: "16px" }}>
            <OwcInput
              label="Enter group ID *"
              size="small"
              style={{ flex: 1 }}
              name="groupId"
              value={formik.values?.groupId || ""}
              onInputChange={formik.handleChange}
              onBlur={formik.handleBlur}
              required={formik.values?.useCIDMMembership}
            />

            <OwcInput
              label="More info url"
              size="small"
              style={{ flex: 1 }}
              name="moreInfoUrl"
              value={formik.values?.moreInfoUrl || ""}
              onInputChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </div>
        </>
      )}
    </ConfirmDialog>
  );
};

export default withApollo(GroupDialog);
